import { cn } from '@/utils';
import { useState, useMemo, ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface PopoverMenuProps {
  /**
   * A reference to the parent element to position the popover; if provided,
   * the popover will render using React's `createPortal` and append it to the body.
   */
  parentRef?: React.RefObject<HTMLDivElement>;

  /**
   * The content to display in the popover.
   */
  content: ReactNode;

  /**
   * Additional class names for the popover wrapper element.
   */
  className?: string;

  /**
   * Additional class names for the rendered popover.
   */
  popoverClassName?: string;

  /**
   * The child element that will trigger the popover on hover.
   */
  children?: React.ReactNode;

  /**
   * Whether the popover should be activated on click instead of hover. If true,
   * the popover will toggle visibility on click and will not hide on mouse leave.
   *
   * Note: When `activateOnClick` is true, the popover will only hide when clicking
   * outside of it or the trigger element.
   */
  activateOnClick?: boolean;

  /**
   * Callback function that is called when the popover is closed.
   */
  onClose?: () => void;
}

/**
 * PopoverMenu component for displaying a popover on hover.
 */
export const PopoverMenu: React.FC<PopoverMenuProps> = ({
  parentRef,
  content,
  className,
  popoverClassName,
  children,
  activateOnClick = false,
  onClose,
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const wasOpenRef = useRef(false);

  // Handle clicks outside the popover and trigger to close
  // the popover when `activateOnClick` is true
  useEffect(() => {
    if (!activateOnClick || !showPopover) return;

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;

      const clickedInsideTrigger = wrapperRef.current?.contains(target);
      const clickedInsidePopover = popoverRef.current?.contains(target);

      if (!clickedInsideTrigger && !clickedInsidePopover) {
        setShowPopover(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setShowPopover(false);
    });

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', (event) => {
        if (event.key === 'Escape') setShowPopover(false);
      });
    };
  }, [activateOnClick, showPopover, onClose]);

  // Call onClose callback only when popover transitions from open to closed
  useEffect(() => {
    if (wasOpenRef.current && !showPopover) {
      onClose?.();
    }

    wasOpenRef.current = showPopover;
  }, [showPopover, onClose]);

  /**
   * Class names for the tooltip element.
   */
  const popoverClassNames = cn(
    [
      'bg-neutral-white text-neutral-black',
      'cursor-default user-select-none',
      'rounded-[0.25rem]',
      'shadow-[0px_4px_24px_0px_#00000040]',
      'w-[285px] h-auto max-h-[150px] overflow-auto',
      'p-4 mt-2.5',
    ],
    {
      'absolute right-0 z-10': !parentRef,
      'fixed z-[50]': parentRef,
    },
    popoverClassName,
  );

  /**
   * Calculates the position of the tooltip based on the parent element's position.
   * Memoize tooltip position calculation to avoid unnecessary DOM queries.
   */
  const popoverPosition = useMemo(() => {
    if (!showPopover || !parentRef || !parentRef.current) {
      return { top: 0, left: 0, visible: false };
    }

    const rect = parentRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    // Tooltip dimensions (should match the CSS width/padding)
    const TOOLTIP_WIDTH = 285;
    const TOOLTIP_HEIGHT = 150; // Approximate, adjust if your tooltip is taller

    // Calculate initial position (right-aligned to the parent, below the parent, with 8px gap)
    let right = rect.right + window.scrollX;
    let top = rect.bottom + window.scrollY + 8;

    // Clamp horizontally so tooltip never goes outside viewport
    const minRight = window.scrollX + TOOLTIP_WIDTH + 8;
    const maxRight = viewportWidth + window.scrollX - 8;
    if (right < minRight) right = minRight;
    if (right > maxRight) right = maxRight;

    let left = right - TOOLTIP_WIDTH;

    // Clamp vertically so tooltip never goes outside viewport
    if (top + TOOLTIP_HEIGHT > viewportHeight + window.scrollY - 8) {
      // If not enough space below, try to show above the parent
      const aboveTop = rect.top + window.scrollY - TOOLTIP_HEIGHT - 8;
      if (aboveTop > window.scrollY + 8) {
        top = aboveTop;
      } else {
        // If not enough space above, clamp to bottom edge
        top = viewportHeight + window.scrollY - TOOLTIP_HEIGHT - 8;
      }
    }

    if (top < window.scrollY + 8) top = window.scrollY + 8;

    return {
      top,
      left,
      visible: true,
    };
  }, [showPopover, parentRef]);

  const handleMouseEnter = () => {
    if (!activateOnClick) setShowPopover(true);
  };

  const handleMouseLeave = () => {
    if (!activateOnClick) setTimeout(() => setShowPopover(false), 200);
  };

  const handleTriggerClick = () => {
    if (activateOnClick) {
      if (showPopover) setShowPopover(false);
      else setShowPopover(true);
    }
  };

  /**
   * Content for the popover.
   */
  const popoverContent = (
    <div
      className={popoverClassNames}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={popoverRef}
    >
      {content}
    </div>
  );

  return (
    <div
      className={cn(className)}
      onClick={handleTriggerClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={wrapperRef}
    >
      {children}
      {showPopover &&
        (() => {
          let portal = null;
          if (parentRef && popoverPosition.visible) {
            const portalRoot = document?.getElementById('portal-root');
            if (portalRoot) {
              portal = createPortal(
                <div
                  className={popoverClassNames}
                  style={{ top: popoverPosition.top, left: popoverPosition.left }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  ref={popoverRef}
                >
                  {content}
                </div>,
                portalRoot,
              );
            }
          }

          return portal ?? popoverContent;
        })()}
    </div>
  );
};

PopoverMenu.displayName = 'PopoverMenu';

export default PopoverMenu;
